import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Stack,
    Alert
} from '@mui/material';

import newsletterImg from '../../assets/news/newsletter.png';
import { apiFetch } from '../../utils/api';

export default function NewsletterSignup() {
    const [email, setEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [resent, setResent] = useState(false);

    const submitToAPI = async (addr) => {
        // POST /api/newsletter (server throttles re-sends)
        const res = await apiFetch('/api/newsletter', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: addr.trim() })
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data?.error || 'Subscription failed. Please try again.');
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        setResent(false);

        try {
            await submitToAPI(email);
            setSuccess(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const onResend = async () => {
        if (!email) return;
        setBusy(true);
        setError('');
        try {
            await submitToAPI(email); // idempotent; backend throttles via next_resend_at
            setResent(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Paper
            elevation={1}
            sx={{
                p: { xs: 2.5, sm: 3, md: 4 },
                borderRadius: 2
            }}
        >
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={{ xs: 2.5, md: 3 }}
                alignItems="center"
            >
                {/* Image */}
                <Box
                    component="img"
                    src={newsletterImg}
                    alt="Newsletter"
                    loading="lazy"
                    decoding="async"
                    sx={{
                        width: { xs: '100%', md: 280 },
                        maxWidth: 420,
                        height: 'auto',
                        borderRadius: 1,
                        boxShadow: { xs: 1, md: 0 }
                    }}
                />

                {/* Form */}
                <Box component="form" onSubmit={onSubmit} sx={{ flex: 1, width: '100%' }}>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            mb: 1,
                            fontSize: { xs: '1.25rem', md: '1.5rem' }
                        }}
                    >
                        Subscribe to our newsletter
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Get occasional updates about exhibits and events.
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Thanks! Please check your email to confirm your subscription.
                        </Alert>
                    )}
                    {resent && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            A new confirmation email has been sent.
                        </Alert>
                    )}

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <TextField
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Email address *"
                            required
                            autoComplete="email"
                            fullWidth
                            size="medium"
                            inputProps={{
                                maxLength: 120,
                                inputMode: 'email',
                                style: { fontSize: '0.95rem' }
                            }}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={busy}
                            sx={{ px: 3, py: 1.25, textTransform: 'none', fontWeight: 700 }}
                        >
                            {busy ? 'Submitting…' : 'Subscribe'}
                        </Button>
                    </Stack>

                    {/* Only show “Resend” AFTER a successful submit */}
                    {success && (
                        <Button
                            variant="text"
                            onClick={onResend}
                            disabled={busy}
                            sx={{ mt: 1.5, textTransform: 'none' }}
                        >
                            {busy ? 'Sending…' : 'Resend confirmation email'}
                        </Button>
                    )}
                </Box>
            </Stack>
        </Paper>
    );
}
